/**
 * Super-admin "Past shows" — browse signed-off archives (retained ~30 days), open
 * one for a post-production audit (transcript + event timeline + version history),
 * and download the transcript or the full archive.
 */
import { useEffect, useState } from 'react';
import {
  downloadBlob,
  getArchive,
  listArchives,
  transcriptToText,
  type ArchiveSummary,
  type SessionArchive,
} from '../lib/archives.js';

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleString('en-AU');
}
function fmtDuration(a: { startedAt: number; endedAt: number }): string {
  const s = Math.max(0, Math.floor((a.endedAt - a.startedAt) / 1000));
  const m = Math.floor(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m ${s % 60}s`;
}
function offset(a: { startedAt: number }, at: number): string {
  const s = Math.max(0, Math.floor((at - a.startedAt) / 1000));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export function AdminArchives({ onError }: { onError?: (m: string) => void }) {
  const [items, setItems] = useState<ArchiveSummary[]>([]);
  const [open, setOpen] = useState<SessionArchive | null>(null);

  const refresh = () =>
    listArchives()
      .then(setItems)
      .catch((e) => onError?.(String((e as Error).message)));

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openShow = async (id: string) => {
    const a = await getArchive(id);
    if (a) setOpen(a);
    else onError?.('Could not open that archive.');
  };

  return (
    <section className="card stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Past shows ({items.length})</h3>
        <button onClick={() => void refresh()}>Refresh</button>
      </div>
      {items.length === 0 && <p className="muted" style={{ margin: 0 }}>No archived shows yet.</p>}
      {items.map((a) => (
        <div key={a.id} className="row" style={{ justifyContent: 'space-between', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.88rem' }}>
            <strong>{a.title}</strong>{' '}
            <span className="muted">
              · {a.ownerName ?? 'Unknown'} · {fmtDate(a.endedAt)}
            </span>
            <br />
            <span className="muted" style={{ fontSize: '0.75rem' }}>
              {a.recording ? `📝 ${a.transcriptLines} lines` : 'no transcript'} · {a.versionCount} versions ·{' '}
              {fmtDuration(a)}
            </span>
          </span>
          <span className="row" style={{ gap: '0.3rem' }}>
            <button onClick={() => void openShow(a.id)}>Open</button>
          </span>
        </div>
      ))}
      <p className="muted" style={{ fontSize: '0.78rem', margin: 0 }}>
        Archives (and any transcripts) are kept for 30 days, then automatically deleted.
      </p>

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(null)}>
          <div
            className="modal card stack"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 820, width: '94vw', maxHeight: '90vh', overflow: 'auto' }}
          >
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>{open.title}</h3>
              <button onClick={() => setOpen(null)} aria-label="Close">×</button>
            </div>
            <div className="muted" style={{ fontSize: '0.82rem' }}>
              {open.ownerName ?? 'Unknown'} · room {open.roomCode} · {fmtDate(open.startedAt)} · {fmtDuration(open)}
            </div>
            <div className="row">
              <button
                className="primary"
                onClick={() => downloadBlob(`${open.title || 'show'}-transcript.txt`, transcriptToText(open), 'text/plain')}
              >
                Download transcript
              </button>
              <button
                onClick={() => downloadBlob(`${open.title || 'show'}.json`, JSON.stringify(open, null, 2), 'application/json')}
              >
                Download full archive
              </button>
            </div>

            <details open>
              <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
                Transcript ({open.transcript.length})
              </summary>
              {open.transcript.length === 0 ? (
                <p className="muted">No transcript was captured (recording was off).</p>
              ) : (
                <div className="card" style={{ maxHeight: '34vh', overflow: 'auto', lineHeight: 1.5 }}>
                  {open.transcript.map((l, i) => (
                    <div key={i}>
                      <span className="muted" style={{ fontFamily: 'monospace', marginRight: '0.5rem' }}>
                        {offset(open, l.at)}
                      </span>
                      {l.text}
                    </div>
                  ))}
                </div>
              )}
            </details>

            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 700 }}>Timeline ({open.events.length})</summary>
              <div className="card" style={{ maxHeight: '28vh', overflow: 'auto', lineHeight: 1.5 }}>
                {open.events.map((e, i) => (
                  <div key={i} style={{ fontSize: '0.85rem' }}>
                    <span className="muted" style={{ fontFamily: 'monospace', marginRight: '0.5rem' }}>
                      {offset(open, e.at)}
                    </span>
                    <strong>{e.kind}</strong>
                    {e.data ? <span className="muted"> — {JSON.stringify(e.data)}</span> : null}
                  </div>
                ))}
              </div>
            </details>

            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
                Script versions ({open.versions.length})
              </summary>
              <div className="card" style={{ maxHeight: '24vh', overflow: 'auto' }}>
                {open.versions.map((v) => (
                  <div key={v.at} style={{ fontSize: '0.85rem' }}>
                    <span className="muted" style={{ fontFamily: 'monospace', marginRight: '0.5rem' }}>
                      {offset(open, v.at)}
                    </span>
                    v{v.version} · {v.title}
                    {v.by ? ` · ${v.by}` : ''}
                  </div>
                ))}
              </div>
            </details>
          </div>
        </div>
      )}
    </section>
  );
}
